import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customBilling, UNIT_KIND_LABELS } from '@/api/customBilling';
import type { BillableItemRow, PricingRuleRow } from '@/api/customBilling';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/lib/auth-store';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';

function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

/**
 * Reseller-facing pricing page: each available billable item shows a row with
 * a toggle (enabled/disabled) and an editable unit price. Resellers choose
 * which items to charge their customers for and at what rate.
 *
 * Super admin: same view, minus the tenant scoping (rule.reseller_id is null
 * for owner→reseller rules).
 */
export function PricingRulesPage() {
  const user = useCurrentUser();
  const tenant = useAuth((s) => s.tenant);
  const qc = useQueryClient();

  // Item scope we care about: reseller_owner manages reseller_to_customer rules.
  // super_admin defaults to owner_to_reseller. Both can flip via a scope switch.
  const [scope, setScope] = useState<'reseller_to_customer' | 'owner_to_reseller'>(
    user.role === 'super_admin' ? 'owner_to_reseller' : 'reseller_to_customer',
  );

  const { data: itemsData } = useQuery({
    queryKey: ['billableItems', scope],
    queryFn: () => customBilling.listItems({ scope, active_only: true, limit: 500 }),
  });
  const { data: rulesData } = useQuery({
    queryKey: ['pricingRules', tenant?.resellerId, scope],
    queryFn: () => customBilling.listRules({
      limit: 500,
      // reseller_owner: their own rules. super_admin: null reseller (owner-level rules).
      reseller_id: user.role === 'super_admin'
        ? (scope === 'owner_to_reseller' ? '' : undefined)
        : (tenant?.resellerId ?? undefined),
    }),
  });

  const items = itemsData?.rows ?? [];
  const rules = rulesData?.rows ?? [];

  // Map item → its rule (if any, scoped to reseller/null).
  const ruleByItem = useMemo(() => {
    const m = new Map<string, PricingRuleRow>();
    for (const r of rules) {
      if (r.customer_id) continue; // customer overrides shown in a later view
      m.set(r.billable_item_id, r);
    }
    return m;
  }, [rules]);

  return (
    <>
      <PageHeader
        eyebrow="Custom billing"
        title="Pricing rules"
        description={
          user.role === 'super_admin'
            ? 'Set what the platform charges each reseller (owner→reseller) or review the reseller→customer items you made available.'
            : 'Choose which items to charge your customers for and set your markup. Defaults come from the platform owner\u2019s prices.'
        }
        actions={user.role === 'super_admin' && (
          <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setScope('owner_to_reseller')}
              className={cn(
                'rounded px-2.5 py-1.5 font-medium transition-colors',
                scope === 'owner_to_reseller' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              Owner → Reseller
            </button>
            <button
              type="button"
              onClick={() => setScope('reseller_to_customer')}
              className={cn(
                'rounded px-2.5 py-1.5 font-medium transition-colors',
                scope === 'reseller_to_customer' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              Reseller → Customer
            </button>
          </div>
        )}
      />

      <div className="space-y-3 max-w-4xl">
        {items.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
            No billable items defined for this scope yet.
          </CardContent></Card>
        ) : (
          items.map((item) => (
            <ItemRuleCard
              key={item.id}
              item={item}
              rule={ruleByItem.get(item.id) ?? null}
              resellerId={user.role === 'super_admin' && scope === 'owner_to_reseller' ? null : tenant?.resellerId ?? null}
              onChanged={() => qc.invalidateQueries({ queryKey: ['pricingRules'] })}
            />
          ))
        )}
      </div>
    </>
  );
}

function ItemRuleCard({
  item, rule, resellerId, onChanged,
}: {
  item: BillableItemRow;
  rule: PricingRuleRow | null;
  resellerId: string | null;
  onChanged: () => void;
}) {
  const [draftPriceCents, setDraftPriceCents] = useState(
    rule?.unit_price_cents ?? item.default_unit_price_cents,
  );
  const [enabled, setEnabled] = useState(rule?.enabled ?? false);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await customBilling.upsertRule({
        billable_item_id: item.id,
        reseller_id: resellerId,
        customer_id: null,
        unit_price_cents: draftPriceCents,
        enabled,
      });
      onChanged();
    } finally { setSaving(false); }
  }

  const dirty = rule === null
    ? (enabled || draftPriceCents !== item.default_unit_price_cents)
    : (enabled !== rule.enabled || draftPriceCents !== rule.unit_price_cents);

  return (
    <Card className={cn(!enabled && 'opacity-80')}>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              {item.name}
              <Badge variant="outline" className="text-[10px]">{UNIT_KIND_LABELS[item.unit_kind]}</Badge>
              {rule?.enabled && <Badge variant="default" className="text-[10px]">Charging</Badge>}
            </CardTitle>
            {item.description && <CardDescription className="mt-1">{item.description}</CardDescription>}
            <p className="text-xs text-muted-foreground mt-1">
              Platform default: <span className="font-mono">{formatMoney(item.default_unit_price_cents)}</span>
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="accent-primary h-4 w-4"
            />
            Enabled
          </label>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Unit price (cents)</label>
            <Input
              type="number" min={0}
              value={draftPriceCents}
              onChange={(e) => setDraftPriceCents(parseInt(e.target.value, 10) || 0)}
              className="w-32 font-mono text-sm"
            />
          </div>
          <div className="text-sm text-slate-600 pb-2.5">= {formatMoney(draftPriceCents)}</div>
          <div className="ms-auto pb-0.5">
            <Button
              size="sm"
              disabled={!dirty || saving}
              onClick={save}
            >
              {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
